class ProductCardMediaSliderComponent extends HTMLElement {
  constructor() {
    super();

    this.allImagesRendered = false;
    this.initialized = false;
    this.observerStarted = false;
    this.mobileQuery = window.matchMedia('(max-width: 989px)');

    this.startWhenUseful();
  }

  startWhenUseful() {
    if (!this.mobileQuery.matches) {
      this.onMediaChange = (event) => {
        if (event.matches) this.scheduleViewportInit();
      };

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onMediaChange, { once: true });
      } else if (this.mobileQuery.addListener) {
        this.mobileQuery.addListener(this.onMediaChange);
      }

      return;
    }

    this.scheduleViewportInit();
  }

  scheduleViewportInit() {
    if (this.initialized || this.observerStarted) return;
    this.observerStarted = true;

    const initNow = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.init(), { timeout: 1200 });
      } else {
        setTimeout(() => this.init(), 300);
      }
    };

    if ('IntersectionObserver' in window) {
      this.viewportObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.viewportObserver.disconnect();
        initNow();
      }, {
        rootMargin: '500px 0px',
        threshold: 0.01,
      });

      this.viewportObserver.observe(this);
    } else {
      initNow();
    }
  }

  init() {
    if (this.initialized) return;
    if (!this.mobileQuery.matches) return;

    this.initialized = true;

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.hydrateSlide(0);
    this.initPages();

    this.resizeObserver = new ResizeObserver(() => this.initPages());
    this.resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this), { passive: true });
    this.slider.addEventListener('pointerdown', () => this.hydrateAroundCurrent(), { passive: true });
    this.slider.addEventListener('touchstart', () => this.hydrateAroundCurrent(), { passive: true });
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    this.addEventListener('slideChanged', this.renderAllImages.bind(this));
  }

  hydrateSlide(index) {
    if (!this.sliderItems || index < 0 || index >= this.sliderItems.length) return;

    const item = this.sliderItems[index];
    if (!item || item.dataset.cardSliderHydrated === 'true') return;

    const template = item.querySelector('[data-card-slider-image-template]');
    if (!template) {
      item.dataset.cardSliderHydrated = 'true';
      return;
    }

    const placeholder = item.querySelector('[data-card-slider-image-placeholder]');
    const content = template.content.cloneNode(true);

    if (placeholder) {
      placeholder.replaceWith(content);
    } else {
      template.parentNode.insertBefore(content, template);
    }

    template.remove();
    item.dataset.cardSliderHydrated = 'true';
  }

  hydrateAroundCurrent() {
    const currentIndex = Math.max((this.currentPage || 1) - 1, 0);
    this.hydrateSlide(currentIndex);
    this.hydrateSlide(currentIndex + 1);
    this.hydrateSlide(currentIndex - 1);
  }

  hydrateAllSlides() {
    if (!this.sliderItems) return;
    this.sliderItems.forEach((_, index) => this.hydrateSlide(index));
    this.allImagesRendered = true;
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );

    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;

    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );

    this.totalPages =
      this.sliderItemsToShow.length - this.slidesPerPage + 1;

    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton || !this.sliderItemOffset) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage !== previousPage) {
      this.hydrateSlide(this.currentPage - 1);
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement:
              this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;

    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const step = Number(event.currentTarget.dataset.step || 1);
    const direction = event.currentTarget.name === 'next' ? 1 : -1;
    const targetPage = Math.max((this.currentPage || 1) + direction * step, 1);

    this.hydrateSlide(targetPage - 1);

    this.slideScrollPosition =
      direction > 0
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;

    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({ left: position });
  }

  renderAllImages(event) {
    if (event && event.detail?.currentPage) {
      this.hydrateSlide(event.detail.currentPage - 1);
    }
  }

  disconnectedCallback() {
    if (this.viewportObserver) this.viewportObserver.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();

    if (this.onMediaChange) {
      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.onMediaChange);
      } else if (this.mobileQuery.removeListener) {
        this.mobileQuery.removeListener(this.onMediaChange);
      }
    }
  }
}

class ProductCardMediaSlider extends ProductCardMediaSliderComponent {
  constructor() {
    super();
  }

  init() {
    super.init();

    if (!this.initialized || !this.slider) return;

    this.sliderControlWrapper = this.querySelector(
      '[data-card-slider-navigation]'
    );
    this.enableSliderLooping = false;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode =
      this.slider.querySelector('[data-card-slider-slide]');

    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(
        '[data-card-slider-dot]'
      )
    );

    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener('click', this.linkToSlide.bind(this))
    );

    this.slider.addEventListener(
      'scroll',
      this.setSlideVisibility.bind(this),
      { passive: true }
    );

    this.setSlideVisibility();
  }

  update() {
    if (!this.slider || !this.sliderItemOffset) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPage !== previousPage) {
      this.hydrateSlide(this.currentPage - 1);
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement:
              this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    this.sliderControlButtons =
      this.querySelectorAll('[data-card-slider-dot]');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove(
        'product-item__image-slider__dot--active'
      );
      link.removeAttribute('aria-current');
    });

    const activeButton = this.sliderControlButtons[this.currentPage - 1];
    if (activeButton) {
      activeButton.classList.add('product-item__image-slider__dot--active');
      activeButton.setAttribute('aria-current', true);
    }

    if (this.enableSliderLooping) return;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide =
      this.currentPage === this.sliderItemsToShow.length;

    if (isFirstSlide) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (isLastSlide) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  setSlideVisibility() {
    if (!this.sliderItemsToShow) return;

    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');

      if (index === this.currentPage - 1) {
        linkElements.forEach((el) => el.removeAttribute('tabindex'));
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        linkElements.forEach((el) =>
          el.setAttribute('tabindex', '-1')
        );
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();

    const targetIndex = this.sliderControlLinksArray.indexOf(event.currentTarget);
    if (targetIndex < 0) return;

    this.hydrateSlide(targetIndex);

    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (targetIndex + 1 - this.currentPage);

    this.slider.scrollTo({ left: slideScrollPosition });
  }
}

if (!customElements.get('product-card-media-slider')) {
  customElements.define(
    'product-card-media-slider',
    ProductCardMediaSlider
  );
}
